import type { ProjectSummary } from '@paid-ui/queries/search-projects';

import { contractTypeTextMap } from '@paid-ui/constants/contract-type';
import { ContractType } from '@paid-ui/enums/contract';

/**
 * The counters that describe a contract's *type*, paired with the contract type whose
 * label names it. `contractCounters` also carries `payeeContracts` / `payerContracts`,
 * which count the same contracts by the group's role — summing those in would
 * double-count every contract on the project.
 */
const contractTypeCounters = [
  ['headContracts', ContractType.HEAD_CONTRACT],
  ['subContracts', ContractType.SUBCONTRACT],
  ['supplyContracts', ContractType.SUPPLY_CONTRACT],
  ['serviceContracts', ContractType.SERVICE_CONTRACT],
  ['fundingContracts', ContractType.FUNDING_CONTRACT],
] as const;

/**
 * Name a contract type for the card, counted. The labels come from the map the rest of
 * the app uses, so they read as the domain terms people know — "2 Subcontracts", not an
 * abbreviation only we understand. Every label pluralises by suffix.
 *
 * @param contractType - Contract type
 * @param count - How many of them the project has
 * @returns A counted label, e.g. `1 Building contract`
 */
function formatContractCount(contractType: ContractType, count: number) {
  const label = contractTypeTextMap[contractType];
  return `${count} ${count === 1 ? label : `${label}s`}`;
}

export interface ContractSummary {
  total: number;
  executed: number;
  mix: string[];
}

/**
 * Summarise a project's contracts for the card.
 *
 * @param project - Project summary
 * @returns Total and executed counts, plus one label per contract type present
 */
export function getContractSummary(project?: ProjectSummary): ContractSummary {
  const counters = project?.contractCounters;

  if (!counters) {
    return { total: 0, executed: 0, mix: [] };
  }

  const total = contractTypeCounters.reduce((sum, [key]) => sum + (counters[key] ?? 0), 0);
  const mix = contractTypeCounters
    .filter(([key]) => (counters[key] ?? 0) > 0)
    .map(([key, contractType]) => formatContractCount(contractType, counters[key]));

  return { total, executed: counters.executedContracts ?? 0, mix };
}

export interface ClaimProgress {
  paid: number;
  total: number;
  ratio: number;
}

/**
 * Build a claims progress bar from a paid amount and its total.
 *
 * @param paid - Amount already paid or received
 * @param total - Total amount claimed
 * @returns Amounts plus a 0-1 ratio, clamped so an overpayment cannot overflow the bar.
 *   A zero total means nothing was claimed in this direction, and the card draws no bar.
 */
export function getClaimProgress(paid = 0, total = 0): ClaimProgress {
  if (total <= 0) {
    return { paid, total: 0, ratio: 0 };
  }

  return { paid, total, ratio: Math.min(1, Math.max(0, paid / total)) };
}

/**
 * Claims the project's group is owed.
 *
 * @param project - Project summary
 * @returns Claims progress as payee
 */
export function getReceivingProgress(project?: ProjectSummary) {
  return getClaimProgress(project?.totalClaimsReceived, project?.totalClaimsAmountAsPayee);
}

/**
 * Claims the project's group owes.
 *
 * @param project - Project summary
 * @returns Claims progress as payer
 */
export function getPayingProgress(project?: ProjectSummary) {
  return getClaimProgress(project?.totalClaimsPaid, project?.totalClaimsAmountAsPayer);
}
